/**
 * AlertsService
 *
 * Headless service that fetches alerts and invites on app load and at
 * intervals. Uses localStorage and a global singleton to de-dupe calls.
 */
import React, { useState, useEffect, useRef } from 'react';
import { get } from '../../../api/core';
import { SERVER_URL } from '../../../api/core';
import { auth } from '../../../firebase/firebase';
import { useAuth } from '../../../context/auth';

// Constants for localStorage keys and fetch intervals
const LAST_ALERTS_FETCH_KEY = 'lastAlertsFetchTime';
const LAST_INVITES_FETCH_KEY = 'lastInvitesFetchTime';
const FETCH_INTERVAL = 30 * 60 * 1000; // 30 minutes in milliseconds

// Global singleton to prevent duplicate fetching across multiple AlertsService instances
const globalFetchState = {
  isInitialized: false,
  alertsInProgress: false,
  invitesInProgress: false,
  currentUserId: null,
  instanceCount: 0
};

/**
 * A service component that fetches alerts and invites when the application loads.
 * This component doesn't render anything visible but performs background data fetching.
 * It uses localStorage to ensure fetches only happen once per session or after the interval.
 */
function AlertsService() {
  const [loading, setLoading] = useState(false);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const { user } = useAuth();
  const instanceId = useRef(Math.random().toString(36).substring(7)); // Unique instance ID

  // Track instance creation and destruction
  useEffect(() => {
    globalFetchState.instanceCount++;
    console.log(`[AlertsService-${instanceId.current}] Instance created. Total instances: ${globalFetchState.instanceCount}`);
    
    return () => {
      globalFetchState.instanceCount--;
      console.log(`[AlertsService-${instanceId.current}] Instance destroyed. Remaining instances: ${globalFetchState.instanceCount}`);
    };
  }, []);

  // Fetch alerts and invites based on user and time interval
  useEffect(() => {
    if (!user) {
      console.log(`[AlertsService-${instanceId.current}] No user, resetting fetch tracker.`);
      // Reset global state when user logs out
      if (globalFetchState.currentUserId) {
        globalFetchState.isInitialized = false;
        globalFetchState.alertsInProgress = false;
        globalFetchState.invitesInProgress = false;
        globalFetchState.currentUserId = null;
      }
      return;
    }

    // Check if user changed (new login)
    if (globalFetchState.currentUserId !== user.uid) {
      console.log(`[AlertsService-${instanceId.current}] User changed, resetting global fetch state`);
      globalFetchState.isInitialized = false;
      globalFetchState.alertsInProgress = false;
      globalFetchState.invitesInProgress = false;
      globalFetchState.currentUserId = user.uid;
    }

    const now = Date.now();

    if (!globalFetchState.isInitialized) {
      // --- Initial Fetch (only one instance should do this) ---
      console.log(`[AlertsService-${instanceId.current}] User detected, checking if initial fetch needed.`);
      globalFetchState.isInitialized = true;

      // Fetch alerts immediately (with singleton check)
      console.log(`[AlertsService-${instanceId.current}] Triggering initial fetchAlerts...`);
      localStorage.setItem(LAST_ALERTS_FETCH_KEY, now.toString()); // Update timestamp before fetch
      fetchAlerts();

      // Fetch invites immediately (with singleton check)
      console.log(`[AlertsService-${instanceId.current}] Triggering initial fetchInvites...`);
      localStorage.setItem(LAST_INVITES_FETCH_KEY, now.toString()); // Update timestamp before fetch
      fetchInvites();

    } else {
      // --- Subsequent Checks (interval-based, only if initial fetch is done) ---
      console.log(`[AlertsService-${instanceId.current}] Global state already initialized, checking intervals.`);

      // Check and fetch alerts based on interval
      const lastAlertsFetch = parseInt(localStorage.getItem(LAST_ALERTS_FETCH_KEY) || '0');
      if (now - lastAlertsFetch > FETCH_INTERVAL) {
        console.log(`[AlertsService-${instanceId.current}] Interval check: Time for alerts fetch (last: ${new Date(lastAlertsFetch)}, now: ${new Date(now)}). Fetching...`);
        localStorage.setItem(LAST_ALERTS_FETCH_KEY, now.toString()); // Update timestamp before fetch
        fetchAlerts();
      } else {
         console.log(`[AlertsService-${instanceId.current}] Interval check: Skipping alerts fetch (last: ${new Date(lastAlertsFetch)}, now: ${new Date(now)}).`);
      }

      // Check and fetch invites based on interval
      const lastInvitesFetch = parseInt(localStorage.getItem(LAST_INVITES_FETCH_KEY) || '0');
      if (now - lastInvitesFetch > FETCH_INTERVAL) {
        console.log(`[AlertsService-${instanceId.current}] Interval check: Time for invites fetch (last: ${new Date(lastInvitesFetch)}, now: ${new Date(now)}). Fetching...`);
        localStorage.setItem(LAST_INVITES_FETCH_KEY, now.toString()); // Update timestamp before fetch
        fetchInvites();
      } else {
        console.log(`[AlertsService-${instanceId.current}] Interval check: Skipping invites fetch (last: ${new Date(lastInvitesFetch)}, now: ${new Date(now)}).`);
      }
    }

  }, [user]); // Depend only on user

  async function fetchAlerts() {
    // Global singleton check to prevent duplicate calls
    if (globalFetchState.alertsInProgress) {
      console.log(`[AlertsService-${instanceId.current}] Alerts fetch already in progress, skipping...`);
      return;
    }
    
    if (!user) return; 
    
    console.log(`[AlertsService-${instanceId.current}] Executing fetchAlerts...`);
    globalFetchState.alertsInProgress = true;
    
    try {
      setLoading(true);
      // Force fetch here since the time check passed OR it's the initial fetch
      const data = await get('/alerts', { useCache: true, force: true }); 
      console.log(`[AlertsService-${instanceId.current}] Alerts fetched successfully.`);
      
      // Store unread count in localStorage for badge display
      if (data && Array.isArray(data)) {
        const unreadCount = data.filter(alert => !alert.read).length;
        localStorage.setItem('unreadAlertsCount', unreadCount.toString());
        console.log(`[AlertsService-${instanceId.current}] Updated unreadAlertsCount: ${unreadCount}`);
        updateTotalNotificationCount();
      } else {
        // Ensure count is zero if no data received
        localStorage.setItem('unreadAlertsCount', '0');
        console.log(`[AlertsService-${instanceId.current}] No alerts data received, setting unreadAlertsCount to 0.`);
        updateTotalNotificationCount();
      }
    } catch (err) {
      console.error(`[AlertsService-${instanceId.current}] Error fetching alerts:`, err);
      // Consider rolling back timestamp only if it wasn't the initial fetch?
      // For simplicity, let's not rollback for now. The interval will just take longer.
    } finally {
      setLoading(false);
      globalFetchState.alertsInProgress = false;
    }
  }

  async function fetchInvites() {
    // Global singleton check to prevent duplicate calls
    if (globalFetchState.invitesInProgress) {
      console.log(`[AlertsService-${instanceId.current}] Invites fetch already in progress, skipping...`);
      return;
    }
    
    if (!user) return;
    
    console.log(`[AlertsService-${instanceId.current}] Executing fetchInvites...`);
    globalFetchState.invitesInProgress = true;
    
    try {
      setLoadingInvites(true);
      const idToken = await auth.currentUser.getIdToken();
      
      // Use apiRequest directly for invites as caching might be less critical
      // or implement similar cache logic for raw fetch if needed.
      // Consider adding cache-busting param if needed: ?_=${Date.now()}
      const response = await fetch(`${SERVER_URL}/invites`, { 
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Cache-Control': 'no-cache' // Explicitly ask server/browser not to cache this
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch invites: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log(`[AlertsService-${instanceId.current}] Invites fetched successfully.`);
      
      // Store invites count in localStorage for badge display
      if (data && Array.isArray(data)) {
        localStorage.setItem('invitesCount', data.length.toString());
        console.log(`[AlertsService-${instanceId.current}] Updated invitesCount: ${data.length}`);
        
        // Save formatted invites to localStorage for AlertsDropdown to use
        const formattedInvites = data.map(invite => {
          return {
            id: invite.id,
            namespaceId: invite.namespaceId,
            namespaceName: invite.namespaceName || 'Unnamed Namespace',
            permission: invite.permission || 'read/write',
            time: formatTimeAgo(invite.createdAt),
            createdAt: invite.createdAt // Keep original timestamp if needed
          };
        });
        
        localStorage.setItem('cachedInvites', JSON.stringify(formattedInvites));
        updateTotalNotificationCount();
      } else {
        // Ensure count is zero and cache is empty if no data received
        localStorage.setItem('invitesCount', '0');
        localStorage.setItem('cachedInvites', '[]');
        console.log(`[AlertsService-${instanceId.current}] No invites data received, setting invitesCount to 0 and clearing cache.`);
        updateTotalNotificationCount();
      }
    } catch (err) {
      console.error(`[AlertsService-${instanceId.current}] Error fetching invites:`, err);
      // Consider rolling back timestamp only if it wasn't the initial fetch?
    } finally {
      setLoadingInvites(false);
      globalFetchState.invitesInProgress = false;
    }
  }
  
  // Helper function to update the total notification count
  function updateTotalNotificationCount() {
    const unreadAlerts = parseInt(localStorage.getItem('unreadAlertsCount') || '0');
    const invitesCount = parseInt(localStorage.getItem('invitesCount') || '0');
    const totalCount = unreadAlerts + invitesCount;
    
    localStorage.setItem('totalNotificationCount', totalCount.toString());
    console.log(`[AlertsService-${instanceId.current}] Updated totalNotificationCount: ${totalCount}`);
  }
  
  // Simple time formatter for invites timestamp
  function formatTimeAgo(timestamp) {
    if (!timestamp) return 'Unknown';
    
    // Convert timestamp to Date if it's a Firestore timestamp object
    let date;
    if (timestamp && typeof timestamp === 'object' && timestamp._seconds !== undefined) {
      date = new Date(timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1000000);
    } else if (timestamp && typeof timestamp === 'object' && timestamp.seconds !== undefined) {
      date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
    } else {
      // Attempt to parse directly, might be ISO string or milliseconds
      date = new Date(timestamp); 
    }
    
    if (isNaN(date.getTime())) {
       console.warn(`[AlertsService-${instanceId.current}] formatTimeAgo: Could not parse timestamp:`, timestamp);
       return 'Unknown date';
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`; // Shorter format
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`; // Shorter format
    return `${Math.floor(diffInSeconds / 86400)}d ago`; // Shorter format
  }

  // This component doesn't render anything visible
  return null;
}

export default AlertsService; 